// API Base URL configuration:
// - Use VITE_API_BASE_URL if set (production or custom)
// - Otherwise fallback to the stable API custom domain
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.creatix.events/api"

// Export API_BASE_URL so OAuth buttons can use it
export { API_BASE_URL }

// Session token storage — uses localStorage so login persists across tab/browser closes.
// The server session lasts 7 days (SESSION_DURATION_DAYS env var). sessionStorage would
// lose the token on tab close, wasting the server-side session.
const SESSION_TOKEN_KEY = "creatix_session_token"
const LEGACY_TOKEN_KEY = "crowdmeet_session_token"

export function getSessionToken(): string | null {
  if (typeof window === "undefined") return null
  return localStorage.getItem(SESSION_TOKEN_KEY)
}

export function setSessionToken(token: string): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(SESSION_TOKEN_KEY, token)
    localStorage.removeItem(LEGACY_TOKEN_KEY)
  }
}

export function clearSessionToken(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem(SESSION_TOKEN_KEY)
    localStorage.removeItem(LEGACY_TOKEN_KEY)
    sessionStorage.removeItem(SESSION_TOKEN_KEY)
  }
}

/** True when the user is already on an auth screen (avoid redirect loops). */
export function isAuthPagePath(pathname: string): boolean {
  return pathname === "/auth" || pathname.startsWith("/auth/")
}

function isAuthApiUrl(url: string): boolean {
  return url.includes("/auth/")
}

function redirectToAuthAfterSessionExpired(): void {
  if (typeof window === "undefined" || isAuthPagePath(window.location.pathname)) {
    return
  }
  const returnTo = `${window.location.pathname}${window.location.search}`
  const params = new URLSearchParams()
  if (returnTo && returnTo !== "/") {
    params.set("redirect", returnTo)
  }
  const query = params.toString()
  window.location.assign(query ? `/auth?${query}` : "/auth")
}

import { queryClient } from "@/lib/query-client"
import * as Sentry from "@sentry/react"

export class ApiError extends Error {
  status: number
  statusText: string
  data?: unknown

  constructor(status: number, statusText: string, data?: unknown) {
    const errorMsg = data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
      ? data.error
      : `API error: ${statusText}`

    super(errorMsg)
    this.name = "ApiError"
    this.status = status
    this.statusText = statusText
    this.data = data
  }
}

async function handleResponse<T>(res: Response, url: string = ""): Promise<T> {
  // Handle 503 Service Unavailable - API server not running
  if (res.status === 503) {
    try {
      const data = await res.json()
      // If API server is down, return null user for auth endpoints
      if (url.includes("/auth/") || res.url.includes("/auth/")) {
        console.warn("API server not available, returning null user")
        return { user: null } as T
      }
      // For other endpoints, throw error
      throw new ApiError(res.status, res.statusText, data)
    } catch (err) {
      if (err instanceof ApiError) throw err
      // If JSON parsing fails, return null user for auth endpoints
      if (url.includes("/auth/") || res.url.includes("/auth/")) {
        return { user: null } as T
      }
      throw new ApiError(503, "Service Unavailable", { message: "API server is not running" })
    }
  }

  if (res.status === 401) {
    const hadSessionToken = !!getSessionToken()
    clearSessionToken()

    const isSessionProbe =
      url.includes("/auth/session") || res.url.includes("/auth/session")

    // Only wipe the cache when a previously-authenticated session expired.
    // Anonymous 401s (e.g. Layout ticket count) must not clear an in-flight
    // GET /auth/session — that leaves RedirectIfAuthenticated stuck on isLoading
    // and /auth never renders.
    if (hadSessionToken && !isSessionProbe) {
      queryClient.clear()
    }

    if (isAuthApiUrl(url) || isAuthApiUrl(res.url)) {
      const data = await res.json().catch(() => ({ user: null }))
      return (
        typeof data === "object" && data !== null ? data : { user: null }
      ) as T
    }

    if (hadSessionToken) {
      redirectToAuthAfterSessionExpired()
    }

    try {
      const data = await res.json()
      throw new ApiError(401, "Unauthorized", data)
    } catch (err) {
      if (err instanceof ApiError) throw err
      throw new ApiError(401, "Unauthorized")
    }
  }

  if (!res.ok) {
    const dsn = import.meta.env.VITE_SENTRY_DSN
    if (dsn && dsn !== "undefined" && res.status >= 400) {
      Sentry.addBreadcrumb({
        category: "api",
        message: `${res.status} ${url || res.url}`,
        level: res.status >= 500 ? "error" : "warning",
        data: { status: res.status, url: url || res.url },
      })
    }
    const data = await res.json().catch(() => ({}))
    throw new ApiError(res.status, res.statusText, data)
  }

  return res.json()
}

/** Build fetch headers: JSON content-type, Bearer session token, optional UTM headers. */
export function buildApiRequestHeaders(contentType: boolean = true): HeadersInit {
  const headers: HeadersInit = {}

  if (contentType) {
    headers["Content-Type"] = "application/json"
  }

  const sessionToken = getSessionToken()
  if (sessionToken) {
    headers["Authorization"] = `Bearer ${sessionToken}`
  }

  // Attach UTM headers if present in URL
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search)
    const utmSource = params.get("utm_source")
    const utmMedium = params.get("utm_medium")
    const utmCampaign = params.get("utm_campaign")
    if (utmSource) headers["X-UTM-Source"] = utmSource
    if (utmMedium) headers["X-UTM-Medium"] = utmMedium
    if (utmCampaign) headers["X-UTM-Campaign"] = utmCampaign
  }

  return headers
}

async function request<T>(method: string, path: string, data?: unknown): Promise<T> {
  const isFormData = data instanceof FormData
  const url = `${API_BASE_URL}${path}`

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 15_000)

  const options: RequestInit = {
    method,
    headers: buildApiRequestHeaders(!isFormData && method !== "GET"),
    credentials: "include",
    // GET requests use browser default caching; mutations always bypass cache
    cache: method === "GET" ? "default" : "no-store",
    signal: controller.signal,
  }

  if (data !== undefined && method !== "GET") {
    options.body = isFormData ? (data as BodyInit) : JSON.stringify(data)
  }

  try {
    const res = await fetch(url, options)
    return handleResponse<T>(res, url)
  } finally {
    clearTimeout(timeout)
  }
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>("GET", path)
  },
  post<T>(path: string, data?: unknown): Promise<T> {
    return request<T>("POST", path, data)
  },
  put<T>(path: string, data?: unknown): Promise<T> {
    return request<T>("PUT", path, data)
  },
  patch<T>(path: string, data?: unknown): Promise<T> {
    return request<T>("PATCH", path, data)
  },
  delete<T>(path: string, data?: unknown): Promise<T> {
    return request<T>("DELETE", path, data)
  },
}
